/**
 * API 클라이언트
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_CONFIG } from './config';

class ApiClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
    });

    this.setupInterceptors();
  }

  private async refreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    console.log('[API Client] Attempting to refresh token...');

    if (!refreshToken) {
      console.error('[API Client] No refresh token available');
      throw new Error('No refresh token available');
    }

    try {
      // 별도의 axios 인스턴스로 토큰 갱신 요청 (인터셉터 우회)
      const refreshAxios = axios.create({
        baseURL: API_CONFIG.BASE_URL,
      });

      console.log('[API Client] Calling refresh endpoint:', API_CONFIG.BASE_URL + '/admin/refresh');

      const response = await refreshAxios.post<{ data: { accessToken: string; refreshToken: string } }>(
        '/admin/refresh',
        { refreshToken }
      );

      console.log('[API Client] Token refresh successful');

      // 새 토큰 저장
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);

      return response.data.data.accessToken;
    } catch (error) {
      console.error('[API Client] Token refresh failed:', error);
      throw error;
    }
  }

  private setupInterceptors() {
    // 요청 인터셉터
    this.instance.interceptors.request.use(
      (config) => {
        // 인증 토큰이 있다면 헤더에 추가
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // FormData가 아닌 경우에만 Content-Type을 application/json으로 설정
        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json';
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 응답 인터셉터
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        console.log('[API Client] Response error detected:', {
          hasResponse: !!error.response,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: originalRequest?.url,
          retry: originalRequest?._retry,
          hasConfig: !!originalRequest
        });

        // error.config가 없는 경우 처리 불가
        if (!originalRequest) {
          console.error('[API Client] No request config available');
          return Promise.reject(error);
        }

        // 401 에러이고 아직 재시도하지 않은 요청인 경우
        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('[API Client] 401 Unauthorized detected');

          // refresh 엔드포인트 자체의 401 에러는 재시도하지 않음
          if (originalRequest.url?.includes('/admin/refresh')) {
            console.log('[API Client] Refresh token expired - logging out');
            // 리프레시 토큰도 만료됨 - 로그아웃 처리
            this.isRefreshing = false;
            this.refreshPromise = null;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            // 세션 만료 알림
            alert('세션이 만료되었습니다. 다시 로그인해주세요.');

            // 현재 페이지가 로그인 페이지가 아닐 때만 리다이렉트
            if (!window.location.pathname.includes('/admin/login')) {
              window.location.href = '/admin/login';
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          console.log('[API Client] Starting token refresh process');

          try {
            // 이미 토큰 갱신 중이면 기존 Promise 재사용
            if (!this.isRefreshing) {
              this.isRefreshing = true;
              this.refreshPromise = this.refreshAccessToken()
                .finally(() => {
                  // 갱신 완료 후 플래그 초기화 (성공/실패 모두)
                  this.isRefreshing = false;
                  this.refreshPromise = null;
                });
            } else {
              console.log('[API Client] Token refresh already in progress, waiting...');
            }

            const newAccessToken = await this.refreshPromise;

            console.log('[API Client] Retrying original request with new token');
            // 새 토큰으로 원래 요청 재시도
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return this.instance(originalRequest);
          } catch (refreshError) {
            console.error('[API Client] Token refresh failed - logging out');
            // 토큰 갱신 실패 시 로그아웃 처리
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            // 세션 만료 알림
            alert('세션이 만료되었습니다. 다시 로그인해주세요.');

            // 현재 페이지가 로그인 페이지가 아닐 때만 리다이렉트
            if (!window.location.pathname.includes('/admin/login')) {
              window.location.href = '/admin/login';
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
